import { z } from 'zod';
import { IsoDate, MoneyAmount, UuidString } from '../primitives.js';

/**
 * S15-BE.1 + S-FIX-1-BE.D.1 — P&L report DTO.
 *
 * revenue = AR.subtotal; cogs = cogs-engine;
 * opex = AP.subtotal без landed-cost / cogs attachments; tax = tax-summary.
 *
 * S-FIX-1: `fx_gain_loss` теперь required + добавлен `included_kinds` (observability —
 * показывает какие типы FX-корректировок учтены). `schema_version` поднят до '1.1.0'.
 */

// B6 invariant — entity-уровень response-схемы forward-compat (см. zod-strict.test.ts).

export const PnlPeriod = z.object({
  date_from: IsoDate,
  date_to: IsoDate,
});
export type PnlPeriod = z.infer<typeof PnlPeriod>;

export const PnlBreakdownCounterpartyRow = z.object({
  counterparty_id: UuidString,
  counterparty_name_ru: z.string(),
  amount: MoneyAmount,
});
export type PnlBreakdownCounterpartyRow = z.infer<typeof PnlBreakdownCounterpartyRow>;

export const PnlBreakdownItemRow = z.object({
  item_id: UuidString,
  item_sku: z.string(),
  amount: MoneyAmount,
});
export type PnlBreakdownItemRow = z.infer<typeof PnlBreakdownItemRow>;

export const PnlBreakdown = z.object({
  revenue_by_counterparty: z.array(PnlBreakdownCounterpartyRow),
  cogs_by_item: z.array(PnlBreakdownItemRow),
  opex_by_counterparty: z.array(PnlBreakdownCounterpartyRow),
});
export type PnlBreakdown = z.infer<typeof PnlBreakdown>;

export const FxAdjustmentKind = z.enum(['unrealized', 'realized']);
export type FxAdjustmentKind = z.infer<typeof FxAdjustmentKind>;

export const FxGainLoss = z.object({
  unrealized_amount_kzt: z.string(),
  realized_amount_kzt: z.string(),
  /** S-FIX-1-BE.D.1 — обязательно содержит ≥ 1 элемент. */
  included_kinds: z.array(FxAdjustmentKind).min(1),
});
export type FxGainLoss = z.infer<typeof FxGainLoss>;

/**
 * S17-BE.1.5 — basis расчёта P&L:
 * - 'accrual' (default) — по проводкам (revenue=Σ AR.subtotal posted, opex=Σ AP.subtotal posted).
 * - 'cash' — по фактическим платежам (revenue=Σ inflow.customer, opex=Σ outflow.{supplier|employee|other}).
 *   cogs=0 (нет invoice_lines).
 */
export const PnlBasis = z.enum(['accrual', 'cash']);
export type PnlBasis = z.infer<typeof PnlBasis>;

export const PnlReport = z.object({
  period: PnlPeriod,
  revenue: MoneyAmount,
  cogs: MoneyAmount,
  gross_profit: MoneyAmount,
  gross_margin_pct: MoneyAmount,
  opex: MoneyAmount,
  operating_profit: MoneyAmount,
  tax: MoneyAmount,
  net_profit: MoneyAmount,
  net_margin_pct: MoneyAmount,
  breakdown: PnlBreakdown,
  /** S-FIX-1-BE.D.1 — required (не optional). */
  fx_gain_loss: FxGainLoss,
  /**
   * S17-BE.1.5 — basis расчёта. Optional для backward-compat с legacy snapshots
   * (которые писались до S17). При чтении legacy без basis — FE/aggregator подставляют 'accrual'.
   */
  basis: PnlBasis.optional(),
  schema_version: z.string(),
  /** S-FIX-1 риск 4: legacy snapshot без FX полей при чтении из pnl_snapshots. */
  legacy_schema: z.boolean().optional(),
});
export type PnlReport = z.infer<typeof PnlReport>;
